import { mapActions, mapState } from 'vuex';
import moment from 'moment';
import { showError } from '@/helpers/globalHelpers';
import { $EventBus } from '@/main';
import HtmlEditor from '@/components/global/HtmlEditor';
import HtmlViewer from '@/components/global/HtmlViewer';

const ALLOWED_FOOTER_STATUSES = [
	'RESUELTO',
	'CONSULTA',
	'EN ATENCION',
	'PENDIENTE AP',
	'CONSULTA ADICIONAL',
	'CONSULTA EXTERNA',
];

const MAX_FILE_SIZE_MB = 10;
const ALLOWED_EXTENSIONS = [
	'.png',
	'.jpg',
	'.jpeg',
	'.xls',
	'.xlsx',
	'.doc',
	'.docx',
	'.ppt',
	'.pptx',
	'.pdf',
	'.msg',
	'.zip',
];



export default {
	name: 'TicketCommentsRich',

	components: {
		HtmlEditor,
		HtmlViewer,
	},

	props: {
		ticket_attention: { type: Number, required: true },
		request: { type: Object, required: true },
		status: { type: String, default: null },
		userType: { type: String, default: null },
		showSendComment: { type: Boolean, default: false },
	},

	data() {
		return {
			types: [
				'EN ATENCION',
				'CONSULTA',
				'CONSULTA ADICIONAL',
				'CONSULTA EXTERNA',
				'RESUELTO',
			],
			selectedType: 'EN ATENCION',
			commentHtml: '',
			commentError: null,
			files: [],
			loading: false,
			loadingSendMessage: false,
			showMessages: false,
			messages: [],
			company_id: parseInt(localStorage.getItem('company_id')),
			showImageDialog: false,
			selectedImage: null,
			imageZoom: 1,
		};
	},

	computed: {
		...mapState('ticketComment', ['ticketComments']),
		...mapState('auth', ['user']),

		headerTitle() {
			return `Comentarios de ${this.request?.num_ticket || 'Ticket'}`;
		},

		showFooter() {
			return (
				ALLOWED_FOOTER_STATUSES.includes(this.status) &&
				this.showSendComment &&
				!this.loadingSendMessage
			);
		},

		hasContent() {
			if (!this.commentHtml) return false;
			// Quitar HTML para verificar si hay texto/imagen real
			const stripped = this.commentHtml
				.replace(/<img[^>]*>/gi, 'IMG')
				.replace(/<[^>]*>/g, '')
				.trim();
			return stripped.length > 0;
		},

		canSend() {
			return !this.loadingSendMessage && (this.hasContent || this.files.length > 0);
		},
	},

	async mounted() {
		await this.fetchComments();
		this.attachImageClickListeners();
	},

	watch: {
		messages() {
			this.$nextTick(() => {
				this.attachImageClickListeners();
			});
		},
	},

	methods: {
		...mapActions('ticketComment', [
			'listTicketComments',
			'cleanTicketComments',
			'createTicketComment',
		]),

		async fetchComments() {
			try {
				this.loading = true;
				const payload = {
					company_id: this.company_id,
					ticket_attention: this.ticket_attention,
				};
				const { ok, error } = await this.listTicketComments(payload);
				this.loading = false;
				this.showMessages = ok;
				if (ok) {
					this.messages = this.formatMessages(this.ticketComments);
					this.$nextTick(() => this.scrollToBottom());
				} else {
					showError(error);
				}
			} catch (err) {
				this.loading = false;
				console.error(err);
			}
		},

		formatMessages(comments) {
			if (!Array.isArray(comments)) return [];
			return comments.map((c) => {
				const isSystem = c.flag_system_ticket;
				const createdAt = c.created_at ? moment(c.created_at) : null;
				return {
					_id: String(c.id),
					content: c.content || '',
					senderId: isSystem ? 'system' : String(c.creator_id),
					username: isSystem
						? 'Sistema'
						: c.creator?.fullName || c.creator?.name || 'Usuario',
					date: createdAt ? createdAt.format('DD MMMM YYYY') : '',
					timestamp: createdAt ? createdAt.format('HH:mm') : '',
					system: !!isSystem,
					isOwn: !isSystem && String(c.creator_id) === String(this.user?.id),
					files: Array.isArray(c.files)
						? c.files.map((f) => ({
							name: this.getFilenameFromUrl(f.comment_file),
							url: f.comment_file,
						}))
						: [],
				};
			});
		},

		shouldShowDateSeparator(index) {
			if (index === 0) return !!this.messages[0].date;
			return this.messages[index].date !== this.messages[index - 1].date;
		},

		scrollToBottom() {
			const el = this.$refs.messagesContainer;
			if (el) el.scrollTop = el.scrollHeight;
		},

		async sendComment() {
			try {
				if (!this.canSend) return;
				this.commentError = null;
				this.loadingSendMessage = true;

				const formData = new FormData();
				formData.append('ticket_attention', this.ticket_attention);
				formData.append('type', this.selectedType);

				if (this.commentHtml) {
					formData.append('content', this.commentHtml);
				}

				if (Array.isArray(this.files)) {
					this.files.forEach((file, index) => {
						const uniqueName = `${Date.now()}-${index}-${file.name}`;
						formData.append('files', file, uniqueName);
					});
				}

				const { ok, error } = await this.createTicketComment(formData);
				if (ok) {
					this.commentHtml = '';
					this.files = [];
					this.$emit('refresh');
					await this.fetchComments();
				} else {
					showError(error);
				}
			} catch (err) {
				showError('Error enviando comentario');
				console.error(err);
			} finally {
				this.loadingSendMessage = false;
			}
		},

		onFileClick(file) {
			if (!file?.url) return;
			
			// Detectar si es una imagen
			const imageExtensions = ['jpg', 'jpeg', 'png', 'gif', 'bmp', 'webp', 'svg'];
			const ext = this.getFileExtension(file.url).toLowerCase();
			
			if (imageExtensions.includes(ext)) {
				// Abrir en popup con zoom
				this.selectedImage = file;
				this.imageZoom = 1;
				this.showImageDialog = true;
			} else {
				// Para archivos no imagen, descargar
				window.open(file.url, '_blank');
			}
		},

		zoomIn() {
			if (this.imageZoom < 3) {
				this.imageZoom = Math.min(this.imageZoom + 0.2, 3);
			}
		},

		zoomOut() {
			if (this.imageZoom > 1) {
				this.imageZoom = Math.max(this.imageZoom - 0.2, 1);
			}
		},

		attachImageClickListeners() {
			// Agregar event listeners a las imágenes incrustadas en el contenido HTML
			const images = this.$refs.messagesContainer?.querySelectorAll('.ticket-comments-rich__content img');
			if (images && images.length) {
				images.forEach((img) => {
					// Agregar estilo de cursor para indicar que es clickeable
					img.style.cursor = 'pointer';
					img.onclick = (e) => {
						e.stopPropagation();
						this.selectedImage = {
							name: img.alt || 'Imagen',
							url: img.src,
						};
						this.imageZoom = 1;
						this.showImageDialog = true;
					};
				});
			}
		},

		handleFileSelection(event) {
			const selected = event?.target?.files;
			if (!selected || !selected.length) return;
			for (let i = 0; i < selected.length; i++) {
				if (this.isValidFile(selected[i])) {
					this.files.push(selected[i]);
				}
			}
			if (this.$refs.commentFileInput) {
				this.$refs.commentFileInput.value = null;
			}
		},

		removeFile(index) {
			this.files.splice(index, 1);
		},

		isValidFile(file) {
			const maxBytes = MAX_FILE_SIZE_MB * 1024 * 1024;
			if (file?.size >= maxBytes) {
				$EventBus.$emit(
					'showSnack',
					'warning',
					`El archivo excede el tamaño máximo permitido. (${MAX_FILE_SIZE_MB}MB)`
				);
				return false;
			}
			const name = file?.name || '';
			const ext = name.slice(((name.lastIndexOf('.') - 1) >>> 0) + 2).toLowerCase();
			if (!ALLOWED_EXTENSIONS.includes('.' + ext)) {
				$EventBus.$emit(
					'showSnack',
					'warning',
					`La extensión de archivo no es válida (${ALLOWED_EXTENSIONS.join(', ')})`
				);
				return false;
			}
			return true;
		},

		getFilenameFromUrl(url) {
			if (!url) return 'Archivo';
			try {
				const urlObj = new URL(url);
				let name = urlObj.pathname.split('/').pop();
				name = decodeURIComponent(name || '');
				name = name.replace(/^\d+-\d+-/, '').replace(/^\d+-/, '');
				return name || 'Archivo';
			} catch {
				const parts = url.split('/');
				return parts[parts.length - 1] || 'Archivo';
			}
		},

		getFileExtension(url) {
			const filename = this.getFilenameFromUrl(url);
			const parts = filename.split('.');
			return parts.length > 1 ? parts.pop().toLowerCase() : '';
		},

		getFileIcon(url) {
			const ext = this.getFileExtension(url);
			const map = {
				jpg: 'mdi-file-image',
				jpeg: 'mdi-file-image',
				png: 'mdi-file-image',
				gif: 'mdi-file-image',
				bmp: 'mdi-file-image',
				webp: 'mdi-file-image',
				svg: 'mdi-file-image',
				pdf: 'mdi-file-pdf-box',
				doc: 'mdi-file-word',
				docx: 'mdi-file-word',
				xls: 'mdi-file-excel',
				xlsx: 'mdi-file-excel',
				csv: 'mdi-file-excel',
				ppt: 'mdi-file-powerpoint',
				pptx: 'mdi-file-powerpoint',
				zip: 'mdi-folder-zip',
				rar: 'mdi-folder-zip',
				'7z': 'mdi-folder-zip',
			};
			return map[ext] || 'mdi-file-outline';
		},

		getFileIconColor(url) {
			const ext = this.getFileExtension(url);
			const map = {
				jpg: '#9C27B0',
				jpeg: '#9C27B0',
				png: '#9C27B0',
				gif: '#9C27B0',
				bmp: '#9C27B0',
				webp: '#9C27B0',
				svg: '#9C27B0',
				pdf: '#F44336',
				doc: '#2196F3',
				docx: '#2196F3',
				xls: '#4CAF50',
				xlsx: '#4CAF50',
				csv: '#4CAF50',
				ppt: '#FF9800',
				pptx: '#FF9800',
				zip: '#795548',
				rar: '#795548',
				'7z': '#795548',
			};
			return map[ext] || 'primary';
		},
	},

	beforeDestroy() {
		this.cleanTicketComments();
	},
};
